import React from 'react';
import { 
  Calendar, 
  MapPin, 
  DollarSign, 
  Users, 
  Target,
  Play,
  Gift,
  ArrowLeft
} from 'lucide-react';
import { Button } from './design-system/Button';
import { Card, CardHeader, CardContent } from './design-system/Card';
import { Heading1, Heading2, Heading3, Body, BodySmall } from './design-system/Typography';
import { Badge } from './design-system/Badge';
import { ProjectFormData } from '../types/project';

interface ProjectPreviewProps {
  projectData: ProjectFormData;
  onBack: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

const getCategoryLabel = (category: string): string => {
  const categories: Record<string, string> = {
    'technology': 'Tecnología',
    'creative': 'Creatividad',
    'community': 'Comunidad',
    'business': 'Negocios',
    'education': 'Educación',
    'health': 'Salud',
    'environment': 'Medio Ambiente',
    'social': 'Social',
    'sports': 'Deportes',
    'travel': 'Viajes'
  };
  return categories[category] || category;
};

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£'
  };
  return symbols[currency] || currency;
};

const formatAmount = (amount: string, currency: string): string => {
  const symbol = getCurrencySymbol(currency);
  const number = parseFloat(amount);
  return `${symbol}${number.toLocaleString()}`;
};

const getDaysRemaining = (deadline: string): number => {
  const today = new Date();
  const deadlineDate = new Date(deadline);
  const diffTime = deadlineDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getVideoEmbedUrl = (url: string): string => {
  // Convert YouTube URLs to embed format
  if (url.includes('youtube.com/watch?v=')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  // Convert Vimeo URLs to embed format
  if (url.includes('vimeo.com/')) {
    const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
    return `https://player.vimeo.com/video/${videoId}`;
  }
  return url;
};

export const ProjectPreview: React.FC<ProjectPreviewProps> = ({
  projectData,
  onBack,
  onConfirm,
  isSubmitting = false
}) => {
  const daysRemaining = getDaysRemaining(projectData.deadline);
  const targetAmount = parseFloat(projectData.targetAmount);
  const currentAmount = 0; // This would come from actual funding data
  const progressPercentage = (currentAmount / targetAmount) * 100;

  return (
    <div className="max-w-4xl mx-auto p-6">
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          icon={ArrowLeft}
        >
          Volver a Editar
        </Button>
        <div>
          <Heading2>Vista Previa del Proyecto</Heading2>
          <BodySmall className="text-gray-600">
            Así verán tu proyecto los potenciales contribuyentes
          </BodySmall>
        </div>
      </div>

      {/* Project Preview */}
      <div className="space-y-8">
        
        {/* Hero Section */}
        <Card variant="elevated" padding="none">
          {projectData.mainImage && (
            <div className="aspect-video w-full overflow-hidden rounded-t-xl">
              <img
                src={projectData.mainImage}
                alt={projectData.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <Badge variant="info" className="mb-4">
                  {getCategoryLabel(projectData.category)}
                </Badge>
                <Heading1 className="mb-4">{projectData.title}</Heading1>
                <div className="flex items-center gap-4 text-gray-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <BodySmall>{projectData.location}</BodySmall>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <BodySmall>{daysRemaining} días restantes</BodySmall>
                  </div>
                </div>
              </div>
            </div>

            {/* Funding Progress */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {formatAmount('0', projectData.currency)}
                  </div>
                  <BodySmall className="text-gray-600">Recaudado</BodySmall>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {formatAmount(projectData.targetAmount, projectData.currency)}
                  </div>
                  <BodySmall className="text-gray-600">Objetivo</BodySmall>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-1">0</div>
                  <BodySmall className="text-gray-600">Contribuyentes</BodySmall>
                </div>
              </div>
              
              <div className="mt-6">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <BodySmall className="text-gray-600">
                    {progressPercentage.toFixed(1)}% completado
                  </BodySmall>
                  <BodySmall className="text-gray-600">
                    {daysRemaining} días restantes
                  </BodySmall>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Button
              variant="primary"
              size="lg"
              fullWidth
              icon={Target}
              disabled
            >
              Contribuir a este Proyecto
            </Button>
          </div>
        </Card>

        {/* Project Description */}
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <Heading3>Acerca de este Proyecto</Heading3>
          </CardHeader>
          <CardContent>
            <Body className="whitespace-pre-wrap leading-relaxed">
              {projectData.description}
            </Body>
          </CardContent>
        </Card>

        {/* Video */}
        {projectData.videoUrl && (
          <Card variant="elevated" padding="lg">
            <CardHeader>
              <Heading3>Video del Proyecto</Heading3>
            </CardHeader>
            <CardContent>
              <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                <iframe
                  src={getVideoEmbedUrl(projectData.videoUrl)}
                  title="Video del proyecto"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rewards */}
        {projectData.rewards.length > 0 && (
          <Card variant="elevated" padding="lg">
            <CardHeader>
              <Heading3>Recompensas</Heading3>
              <BodySmall>Elige tu nivel de contribución</BodySmall>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {projectData.rewards
                  .sort((a, b) => a.amount - b.amount)
                  .map((reward, index) => (
                  <div
                    key={reward.id}
                    className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="text-2xl font-bold text-blue-600">
                            {formatAmount(reward.amount.toString(), projectData.currency)}
                          </div>
                          <Badge variant="secondary" size="sm">
                            {reward.currentBackers} contribuyentes
                          </Badge>
                        </div>
                        <Heading3 className="mb-2">{reward.title}</Heading3>
                        <Body className="text-gray-600 mb-4">
                          {reward.description}
                        </Body>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>Entrega estimada: {reward.estimatedDelivery}</span>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled
                      >
                        Seleccionar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation Actions */}
      <div className="mt-12 p-6 bg-blue-50 rounded-lg">
        <div className="text-center mb-6">
          <Heading3 className="mb-2">¿Todo se ve bien?</Heading3>
          <Body className="text-gray-600">
            Una vez publicado, tu proyecto será visible para todos los usuarios y podrán comenzar a contribuir.
          </Body>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={onBack}
            variant="secondary"
            size="lg"
            icon={ArrowLeft}
            disabled={isSubmitting}
          >
            Hacer Cambios
          </Button>
          
          <Button
            onClick={onConfirm}
            variant="primary"
            size="lg"
            icon={Target}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Publicando...' : 'Confirmar y Publicar'}
          </Button>
        </div>
      </div>
    </div>
  );
};